var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "mdast-util-mdx";
import "mdast-util-mdx-jsx";
import * as bytemd from "@tarslib/bytemd";
import React, { useRef, useEffect, Fragment, cloneElement, useMemo, useState, useLayoutEffect } from "react";
import { defaultSchema } from "hast-util-sanitize";
import StyleToJS from "style-to-js";
import { getProcessor, getMdxProcessor } from "@tarslib/bytemd-processor";
import { marked } from "marked";
const Editor = ({ onChange, ...props }) => {
  const ed = useRef();
  const el = useRef(null);
  const onChangeRef = useRef();
  useEffect(() => {
    if (!el.current)
      return;
    const editor = new bytemd.Editor({
      target: el.current,
      props
    });
    editor.$on("change", (e) => {
      var _a;
      (_a = onChangeRef.current) == null ? void 0 : _a.call(onChangeRef, e.detail.value);
    });
    ed.current = editor;
    return () => {
      editor.$destroy();
    };
  }, []);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    var _a;
    (_a = ed.current) == null ? void 0 : _a.$set(props);
  }, [props]);
  return /* @__PURE__ */ React.createElement("div", { ref: el });
};
const blockquoteHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "blockquote")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.blockquote, { ...ctx.calcRequireIndexProps(input) }, childrenFactory(node))
  };
};
const breakHandler = ({ componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "break")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.br, { ...ctx.calcRequireIndexProps(input) })
  };
};
const codeHandler = ({ componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "code")
    return input;
  const language = node.lang || "";
  const code = node.value;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.pre, { ...ctx.calcRequireIndexProps(input) }, /* @__PURE__ */ React.createElement(
      componentMap.code,
      {
        className: language ? `language-${language}` : void 0
      },
      code
    ))
  };
};
const defHandler = () => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "definition")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(React.Fragment, null)
  };
};
const deleteHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "delete")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.del, { ...ctx.calcRequireIndexProps(input) }, childrenFactory(node))
  };
};
const emphasisHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "emphasis")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.em, { ...ctx.calcRequireIndexProps(input) }, childrenFactory(node))
  };
};
const frontmatterHandler = () => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type === "yaml") {
    return {
      res: null
    };
  }
  if (node.type === "footnoteReference") {
    return {
      res: null
    };
  }
  if (node.type === "footnoteDefinition") {
    return {
      res: null
    };
  }
  return input;
};
const headingHandler = ({ ctx, childrenFactory, componentMap }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "heading")
    return input;
  const level = node.depth;
  const Tag = `h${level}`;
  const ComponentName = componentMap[Tag];
  return {
    res: /* @__PURE__ */ React.createElement(ComponentName, { ...ctx.calcRequireIndexProps(input) }, childrenFactory(node))
  };
};
const htmlHandler = () => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "html")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(React.Fragment, null)
  };
};
const imageHandler = ({ componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "image")
    return input;
  const src = node.url || "";
  const alt = node.alt || "";
  const title = node.title || "";
  return {
    res: /* @__PURE__ */ React.createElement(
      componentMap.img,
      {
        src,
        alt,
        title,
        ...ctx.calcRequireIndexProps(input)
      }
    )
  };
};
const imgRefHandler = ({ ctx, componentMap }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "imageReference")
    return input;
  const def = ctx.getIdentifierMap().get(node.identifier);
  const href = (def == null ? void 0 : def.url) || "";
  const alt = (def == null ? void 0 : def.label) || "";
  const title = (def == null ? void 0 : def.title) || void 0;
  return {
    res: /* @__PURE__ */ React.createElement(
      componentMap.img,
      {
        src: href,
        title,
        alt,
        ...ctx.calcRequireIndexProps(input)
      }
    )
  };
};
const inlineCodeHandler = ({ componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "inlineCode")
    return input;
  const code = node.value;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.code, { ...ctx.calcRequireIndexProps(input) }, code)
  };
};
const linkHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "link")
    return input;
  const href = node.url || "";
  const title = node.title || "";
  return {
    res: /* @__PURE__ */ React.createElement(
      componentMap.a,
      {
        ...{ href, title },
        ...ctx.calcRequireIndexProps(input)
      },
      childrenFactory(node)
    )
  };
};
const linkRefHandler = ({ ctx, childrenFactory, componentMap }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "linkReference")
    return input;
  const def = ctx.getIdentifierMap().get(node.identifier);
  const href = (def == null ? void 0 : def.url) || "";
  const title = (def == null ? void 0 : def.title) || void 0;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.a, { href, title }, childrenFactory(node))
  };
};
const listHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "list")
    return input;
  if (node.ordered) {
    return {
      res: /* @__PURE__ */ React.createElement(
        componentMap.ol,
        {
          start: node.start,
          ...ctx.calcRequireIndexProps(input)
        },
        childrenFactory(node)
      )
    };
  }
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.ul, null, childrenFactory(node))
  };
};
const listItemHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "listItem")
    return input;
  if ((node == null ? void 0 : node.checked) != null) {
    return {
      res: /* @__PURE__ */ React.createElement(
        componentMap.li,
        {
          "data-checked": node.checked,
          ...ctx.calcRequireIndexProps(input)
        },
        /* @__PURE__ */ React.createElement("input", { disabled: true, type: "checkbox", checked: !!node.checked }),
        /* @__PURE__ */ React.createElement(
          "div",
          {
            style: {
              display: "inline-flex",
              flexDirection: "column"
            }
          },
          childrenFactory(node)
        )
      )
    };
  }
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.li, null, childrenFactory(node))
  };
};
const paragraphHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "paragraph")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.p, { ...ctx.calcRequireIndexProps(input) }, childrenFactory(node))
  };
};
const rootHandler = ({ childrenFactory }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "root")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(Fragment, null, childrenFactory(node))
  };
};
const strongHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "strong")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.strong, { ...ctx.calcRequireIndexProps(input) }, childrenFactory(node))
  };
};
const tableHandler = ({ childrenFactory, componentMap, ctx }) => (input) => {
  var _a;
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "table")
    return input;
  if (node.children.length > 0) {
    const theadRow = node.children[0];
    const count = theadRow.children.length;
    theadRow.children.forEach((val) => {
      val.type = "tableHeadCell";
    });
    const align = (_a = node.align) != null ? _a : [];
    node.children.forEach((child) => {
      let len = child.children.length;
      if (len < count) {
        child.children = child.children.concat(
          Array(count - len).fill(0).map(() => {
            return {
              type: "tableCell",
              children: []
            };
          })
        );
      } else if (len > count) {
        child.children.splice(count, len - count);
      }
      child.children.forEach((val, index) => {
        val.align = align[index];
      });
    });
  }
  const [head, ...body] = React.Children.toArray(childrenFactory(node));
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.table, { ...ctx.calcRequireIndexProps(input) }, head && /* @__PURE__ */ React.createElement(componentMap.thead, null, head), !!(body == null ? void 0 : body.length) && /* @__PURE__ */ React.createElement(componentMap.tbody, null, body))
  };
};
const tableCellHandler = ({ childrenFactory, componentMap }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "tableCell")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.td, { align: node.align }, childrenFactory(node))
  };
};
const tableHeadCellHandler = ({ childrenFactory, componentMap }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "tableHeadCell")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.th, { align: node.align }, childrenFactory(node))
  };
};
const tableRowHandler = ({ childrenFactory, componentMap }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "tableRow")
    return input;
  return {
    res: /* @__PURE__ */ React.createElement(componentMap.tr, { className: "" }, childrenFactory(node))
  };
};
const textHandler = () => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "text")
    return input;
  if (!node.value) {
    return {
      res: null
    };
  }
  return {
    res: node.value
  };
};
const thematicBreakHandler = ({ componentMap, ctx }) => (input) => {
  if ("res" in input)
    return input;
  const node = input.source;
  if (node.type !== "thematicBreak")
    return input;
  const ComponentName = componentMap["hr"];
  return {
    res: /* @__PURE__ */ React.createElement(ComponentName, { ...ctx.calcRequireIndexProps(input) })
  };
};
const tagName = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "dd",
  "del",
  "details",
  "div",
  "dl",
  "dt",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "img",
  "input",
  "ins",
  "kbd",
  "li",
  "ol",
  "p",
  "picture",
  "pre",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "section",
  "source",
  "span",
  "strike",
  "strong",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "tt",
  "ul",
  "var"
];
const VoidElementTagName = [
  "embed",
  "source",
  "img",
  "link",
  "area",
  "base",
  "br",
  "col",
  "hr",
  "keygen",
  "meta",
  "param",
  "track",
  "wbr",
  "menuitem",
  "input"
];
function isVoidElement(tagName2) {
  return VoidElementTagName.includes(tagName2);
}
const possibleStandardNames = {
  accept: "accept",
  acceptcharset: "acceptCharset",
  "accept-charset": "acceptCharset",
  accesskey: "accessKey",
  action: "action",
  allowfullscreen: "allowFullScreen",
  alt: "alt",
  as: "as",
  async: "async",
  autocapitalize: "autoCapitalize",
  autocomplete: "autoComplete",
  autocorrect: "autoCorrect",
  autofocus: "autoFocus",
  autoplay: "autoPlay",
  autosave: "autoSave",
  capture: "capture",
  cellpadding: "cellPadding",
  cellspacing: "cellSpacing",
  challenge: "challenge",
  charset: "charSet",
  checked: "checked",
  children: "children",
  cite: "cite",
  class: "className",
  classid: "classID",
  classname: "className",
  cols: "cols",
  colspan: "colSpan",
  content: "content",
  contenteditable: "contentEditable",
  contextmenu: "contextMenu",
  controls: "controls",
  controlslist: "controlsList",
  coords: "coords",
  crossorigin: "crossOrigin",
  dangerouslysetinnerhtml: "dangerouslySetInnerHTML",
  data: "data",
  datetime: "dateTime",
  default: "default",
  defaultchecked: "defaultChecked",
  defaultvalue: "defaultValue",
  defer: "defer",
  dir: "dir",
  disabled: "disabled",
  disablepictureinpicture: "disablePictureInPicture",
  disableremoteplayback: "disableRemotePlayback",
  download: "download",
  draggable: "draggable",
  enctype: "encType",
  enterkeyhint: "enterKeyHint",
  fetchpriority: "fetchPriority",
  for: "htmlFor",
  form: "form",
  formmethod: "formMethod",
  formaction: "formAction",
  formenctype: "formEncType",
  formnovalidate: "formNoValidate",
  formtarget: "formTarget",
  frameborder: "frameBorder",
  headers: "headers",
  height: "height",
  hidden: "hidden",
  high: "high",
  href: "href",
  hreflang: "hrefLang",
  htmlfor: "htmlFor",
  httpequiv: "httpEquiv",
  "http-equiv": "httpEquiv",
  icon: "icon",
  id: "id",
  imagesizes: "imageSizes",
  imagesrcset: "imageSrcSet",
  innerhtml: "innerHTML",
  inputmode: "inputMode",
  integrity: "integrity",
  is: "is",
  itemid: "itemID",
  itemprop: "itemProp",
  itemref: "itemRef",
  itemscope: "itemScope",
  itemtype: "itemType",
  keyparams: "keyParams",
  keytype: "keyType",
  kind: "kind",
  label: "label",
  lang: "lang",
  list: "list",
  loop: "loop",
  low: "low",
  manifest: "manifest",
  marginwidth: "marginWidth",
  marginheight: "marginHeight",
  max: "max",
  maxlength: "maxLength",
  media: "media",
  mediagroup: "mediaGroup",
  method: "method",
  min: "min",
  minlength: "minLength",
  multiple: "multiple",
  muted: "muted",
  name: "name",
  nomodule: "noModule",
  nonce: "nonce",
  novalidate: "noValidate",
  open: "open",
  optimum: "optimum",
  pattern: "pattern",
  placeholder: "placeholder",
  playsinline: "playsInline",
  poster: "poster",
  preload: "preload",
  profile: "profile",
  radiogroup: "radioGroup",
  readonly: "readOnly",
  referrerpolicy: "referrerPolicy",
  rel: "rel",
  required: "required",
  reversed: "reversed",
  role: "role",
  rows: "rows",
  rowspan: "rowSpan",
  sandbox: "sandbox",
  scope: "scope",
  scoped: "scoped",
  scrolling: "scrolling",
  seamless: "seamless",
  selected: "selected",
  shape: "shape",
  size: "size",
  sizes: "sizes",
  span: "span",
  spellcheck: "spellCheck",
  src: "src",
  srcdoc: "srcDoc",
  srclang: "srcLang",
  srcset: "srcSet",
  start: "start",
  step: "step",
  style: "style",
  summary: "summary",
  tabindex: "tabIndex",
  target: "target",
  title: "title",
  type: "type",
  usemap: "useMap",
  value: "value",
  width: "width",
  wmode: "wmode",
  wrap: "wrap",
  about: "about",
  accentheight: "accentHeight",
  "accent-height": "accentHeight",
  accumulate: "accumulate",
  additive: "additive",
  alignmentbaseline: "alignmentBaseline",
  "alignment-baseline": "alignmentBaseline",
  allowreorder: "allowReorder",
  alphabetic: "alphabetic",
  amplitude: "amplitude",
  arabicform: "arabicForm",
  "arabic-form": "arabicForm",
  ascent: "ascent",
  attributename: "attributeName",
  attributetype: "attributeType",
  autoreverse: "autoReverse",
  azimuth: "azimuth",
  basefrequency: "baseFrequency",
  baselineshift: "baselineShift",
  "baseline-shift": "baselineShift",
  baseprofile: "baseProfile",
  bbox: "bbox",
  begin: "begin",
  bias: "bias",
  by: "by",
  calcmode: "calcMode",
  capheight: "capHeight",
  "cap-height": "capHeight",
  clip: "clip",
  clippath: "clipPath",
  "clip-path": "clipPath",
  clippathunits: "clipPathUnits",
  cliprule: "clipRule",
  "clip-rule": "clipRule",
  color: "color",
  colorinterpolation: "colorInterpolation",
  "color-interpolation": "colorInterpolation",
  colorinterpolationfilters: "colorInterpolationFilters",
  "color-interpolation-filters": "colorInterpolationFilters",
  colorprofile: "colorProfile",
  "color-profile": "colorProfile",
  colorrendering: "colorRendering",
  "color-rendering": "colorRendering",
  contentscripttype: "contentScriptType",
  contentstyletype: "contentStyleType",
  cursor: "cursor",
  cx: "cx",
  cy: "cy",
  d: "d",
  datatype: "datatype",
  decelerate: "decelerate",
  descent: "descent",
  diffuseconstant: "diffuseConstant",
  direction: "direction",
  display: "display",
  divisor: "divisor",
  dominantbaseline: "dominantBaseline",
  "dominant-baseline": "dominantBaseline",
  dur: "dur",
  dx: "dx",
  dy: "dy",
  edgemode: "edgeMode",
  elevation: "elevation",
  enablebackground: "enableBackground",
  "enable-background": "enableBackground",
  end: "end",
  exponent: "exponent",
  externalresourcesrequired: "externalResourcesRequired",
  fill: "fill",
  fillopacity: "fillOpacity",
  "fill-opacity": "fillOpacity",
  fillrule: "fillRule",
  "fill-rule": "fillRule",
  filter: "filter",
  filterres: "filterRes",
  filterunits: "filterUnits",
  floodopacity: "floodOpacity",
  "flood-opacity": "floodOpacity",
  floodcolor: "floodColor",
  "flood-color": "floodColor",
  focusable: "focusable",
  fontfamily: "fontFamily",
  "font-family": "fontFamily",
  fontsize: "fontSize",
  "font-size": "fontSize",
  fontsizeadjust: "fontSizeAdjust",
  "font-size-adjust": "fontSizeAdjust",
  fontstretch: "fontStretch",
  "font-stretch": "fontStretch",
  fontstyle: "fontStyle",
  "font-style": "fontStyle",
  fontvariant: "fontVariant",
  "font-variant": "fontVariant",
  fontweight: "fontWeight",
  "font-weight": "fontWeight",
  format: "format",
  from: "from",
  fx: "fx",
  fy: "fy",
  g1: "g1",
  g2: "g2",
  glyphname: "glyphName",
  "glyph-name": "glyphName",
  glyphorientationhorizontal: "glyphOrientationHorizontal",
  "glyph-orientation-horizontal": "glyphOrientationHorizontal",
  glyphorientationvertical: "glyphOrientationVertical",
  "glyph-orientation-vertical": "glyphOrientationVertical",
  glyphref: "glyphRef",
  gradienttransform: "gradientTransform",
  gradientunits: "gradientUnits",
  hanging: "hanging",
  horizadvx: "horizAdvX",
  "horiz-adv-x": "horizAdvX",
  horizoriginx: "horizOriginX",
  "horiz-origin-x": "horizOriginX",
  ideographic: "ideographic",
  imagerendering: "imageRendering",
  "image-rendering": "imageRendering",
  in2: "in2",
  in: "in",
  inlist: "inlist",
  intercept: "intercept",
  k1: "k1",
  k2: "k2",
  k3: "k3",
  k4: "k4",
  k: "k",
  kernelmatrix: "kernelMatrix",
  kernelunitlength: "kernelUnitLength",
  kerning: "kerning",
  keypoints: "keyPoints",
  keysplines: "keySplines",
  keytimes: "keyTimes",
  lengthadjust: "lengthAdjust",
  letterspacing: "letterSpacing",
  "letter-spacing": "letterSpacing",
  lightingcolor: "lightingColor",
  "lighting-color": "lightingColor",
  limitingconeangle: "limitingConeAngle",
  local: "local",
  markerend: "markerEnd",
  "marker-end": "markerEnd",
  markerheight: "markerHeight",
  markermid: "markerMid",
  "marker-mid": "markerMid",
  markerstart: "markerStart",
  "marker-start": "markerStart",
  markerunits: "markerUnits",
  markerwidth: "markerWidth",
  mask: "mask",
  maskcontentunits: "maskContentUnits",
  maskunits: "maskUnits",
  mathematical: "mathematical",
  mode: "mode",
  numoctaves: "numOctaves",
  offset: "offset",
  opacity: "opacity",
  operator: "operator",
  order: "order",
  orient: "orient",
  orientation: "orientation",
  origin: "origin",
  overflow: "overflow",
  overlineposition: "overlinePosition",
  "overline-position": "overlinePosition",
  overlinethickness: "overlineThickness",
  "overline-thickness": "overlineThickness",
  paintorder: "paintOrder",
  "paint-order": "paintOrder",
  panose1: "panose1",
  "panose-1": "panose1",
  pathlength: "pathLength",
  patterncontentunits: "patternContentUnits",
  patterntransform: "patternTransform",
  patternunits: "patternUnits",
  pointerevents: "pointerEvents",
  "pointer-events": "pointerEvents",
  points: "points",
  pointsatx: "pointsAtX",
  pointsaty: "pointsAtY",
  pointsatz: "pointsAtZ",
  prefix: "prefix",
  preservealpha: "preserveAlpha",
  preserveaspectratio: "preserveAspectRatio",
  primitiveunits: "primitiveUnits",
  property: "property",
  r: "r",
  radius: "radius",
  refx: "refX",
  refy: "refY",
  renderingintent: "renderingIntent",
  "rendering-intent": "renderingIntent",
  repeatcount: "repeatCount",
  repeatdur: "repeatDur",
  requiredextensions: "requiredExtensions",
  requiredfeatures: "requiredFeatures",
  resource: "resource",
  restart: "restart",
  result: "result",
  results: "results",
  rotate: "rotate",
  rx: "rx",
  ry: "ry",
  scale: "scale",
  security: "security",
  seed: "seed",
  shaperendering: "shapeRendering",
  "shape-rendering": "shapeRendering",
  slope: "slope",
  spacing: "spacing",
  specularconstant: "specularConstant",
  specularexponent: "specularExponent",
  speed: "speed",
  spreadmethod: "spreadMethod",
  startoffset: "startOffset",
  stddeviation: "stdDeviation",
  stemh: "stemh",
  stemv: "stemv",
  stitchtiles: "stitchTiles",
  stopcolor: "stopColor",
  "stop-color": "stopColor",
  stopopacity: "stopOpacity",
  "stop-opacity": "stopOpacity",
  strikethroughposition: "strikethroughPosition",
  "strikethrough-position": "strikethroughPosition",
  strikethroughthickness: "strikethroughThickness",
  "strikethrough-thickness": "strikethroughThickness",
  string: "string",
  stroke: "stroke",
  strokedasharray: "strokeDasharray",
  "stroke-dasharray": "strokeDasharray",
  strokedashoffset: "strokeDashoffset",
  "stroke-dashoffset": "strokeDashoffset",
  strokelinecap: "strokeLinecap",
  "stroke-linecap": "strokeLinecap",
  strokelinejoin: "strokeLinejoin",
  "stroke-linejoin": "strokeLinejoin",
  strokemiterlimit: "strokeMiterlimit",
  "stroke-miterlimit": "strokeMiterlimit",
  strokewidth: "strokeWidth",
  "stroke-width": "strokeWidth",
  strokeopacity: "strokeOpacity",
  "stroke-opacity": "strokeOpacity",
  suppresscontenteditablewarning: "suppressContentEditableWarning",
  suppresshydrationwarning: "suppressHydrationWarning",
  surfacescale: "surfaceScale",
  systemlanguage: "systemLanguage",
  tablevalues: "tableValues",
  targetx: "targetX",
  targety: "targetY",
  textanchor: "textAnchor",
  "text-anchor": "textAnchor",
  textdecoration: "textDecoration",
  "text-decoration": "textDecoration",
  textlength: "textLength",
  textrendering: "textRendering",
  "text-rendering": "textRendering",
  to: "to",
  transform: "transform",
  transformorigin: "transformOrigin",
  "transform-origin": "transformOrigin",
  typeof: "typeof",
  u1: "u1",
  u2: "u2",
  underlineposition: "underlinePosition",
  "underline-position": "underlinePosition",
  underlinethickness: "underlineThickness",
  "underline-thickness": "underlineThickness",
  unicode: "unicode",
  unicodebidi: "unicodeBidi",
  "unicode-bidi": "unicodeBidi",
  unicoderange: "unicodeRange",
  "unicode-range": "unicodeRange",
  unitsperem: "unitsPerEm",
  "units-per-em": "unitsPerEm",
  unselectable: "unselectable",
  valphabetic: "vAlphabetic",
  "v-alphabetic": "vAlphabetic",
  values: "values",
  vectoreffect: "vectorEffect",
  "vector-effect": "vectorEffect",
  version: "version",
  vertadvy: "vertAdvY",
  "vert-adv-y": "vertAdvY",
  vertoriginx: "vertOriginX",
  "vert-origin-x": "vertOriginX",
  vertoriginy: "vertOriginY",
  "vert-origin-y": "vertOriginY",
  vhanging: "vHanging",
  "v-hanging": "vHanging",
  videographic: "vIdeographic",
  "v-ideographic": "vIdeographic",
  viewbox: "viewBox",
  viewtarget: "viewTarget",
  visibility: "visibility",
  vmathematical: "vMathematical",
  "v-mathematical": "vMathematical",
  vocab: "vocab",
  widths: "widths",
  wordspacing: "wordSpacing",
  "word-spacing": "wordSpacing",
  writingmode: "writingMode",
  "writing-mode": "writingMode",
  x1: "x1",
  x2: "x2",
  x: "x",
  xchannelselector: "xChannelSelector",
  xheight: "xHeight",
  "x-height": "xHeight",
  xlinkactuate: "xlinkActuate",
  "xlink:actuate": "xlinkActuate",
  xlinkarcrole: "xlinkArcrole",
  "xlink:arcrole": "xlinkArcrole",
  xlinkhref: "xlinkHref",
  "xlink:href": "xlinkHref",
  xlinkrole: "xlinkRole",
  "xlink:role": "xlinkRole",
  xlinkshow: "xlinkShow",
  "xlink:show": "xlinkShow",
  xlinktitle: "xlinkTitle",
  "xlink:title": "xlinkTitle",
  xlinktype: "xlinkType",
  "xlink:type": "xlinkType",
  xmlbase: "xmlBase",
  "xml:base": "xmlBase",
  xmllang: "xmlLang",
  "xml:lang": "xmlLang",
  xmlns: "xmlns",
  "xml:space": "xmlSpace",
  xmlnsxlink: "xmlnsXlink",
  "xmlns:xlink": "xmlnsXlink",
  xmlspace: "xmlSpace",
  y1: "y1",
  y2: "y2",
  y: "y",
  ychannelselector: "yChannelSelector",
  z: "z",
  zoomandpan: "zoomAndPan"
};
function getPossibleStandardName(propName) {
  const lowerCasedName = propName.toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(possibleStandardNames, lowerCasedName)) {
    return null;
  }
  return possibleStandardNames[lowerCasedName] || null;
}
function transformHtmlAttr2JsxAttrName(propName) {
  var _a;
  return (_a = getPossibleStandardName(propName)) != null ? _a : propName;
}
function getChildLastIndex(data) {
  return data == null ? void 0 : data.childLastIndex;
}
function getChildFirstIndex(data) {
  return data == null ? void 0 : data.childFirstIndex;
}
function markFirstChild(childItem) {
  var _a, _b;
  if (childItem.type === "mdxJsxTextElement" || childItem.type === "mdxJsxFlowElement") {
    if (childItem.name) {
      (_a = childItem.data) != null ? _a : childItem.data = {};
      childItem.data.childFirstIndex = true;
    }
    return childItem;
  }
  (_b = childItem.data) != null ? _b : childItem.data = {};
  childItem.data.childFirstIndex = true;
  return childItem;
}
const markLastChild = (childItem) => {
  var _a, _b;
  if (childItem.type === "mdxJsxTextElement" || childItem.type === "mdxJsxFlowElement") {
    if (childItem.name) {
      (_a = childItem.data) != null ? _a : childItem.data = {};
      childItem.data.childLastIndex = true;
    }
    return childItem;
  }
  (_b = childItem.data) != null ? _b : childItem.data = {};
  childItem.data.childLastIndex = true;
  return childItem;
};
const calcRequireIndexProps = (input) => {
  return {
    "data-first": getChildFirstIndex(input.source.data),
    "data-last": getChildLastIndex(input.source.data)
  };
};
function mdastBfs(node, cb) {
  const queue = [node];
  const result = [];
  while (queue.length) {
    const node2 = queue.shift();
    const r = cb && node2 ? cb(node2) : node2;
    if (Array.isArray(r)) {
      queue.push(...r);
    } else if (r) {
      result.push(r);
    }
    if (node2 && "children" in node2 && node2.children) {
      queue.push(...node2.children);
    }
  }
  return result;
}
const tagMap = tagName.reduce((res, key) => {
  res[key] = key;
  return res;
}, {});
function findDefinition(definitions, key) {
  let dataDefault;
  let index = -1;
  if (definitions) {
    while (++index < definitions.length) {
      const entry = definitions[index];
      const name = typeof entry === "string" ? entry : entry[0];
      if (name === key) {
        return entry;
      }
      if (name === "data*")
        dataDefault = entry;
    }
  }
  if (key.length > 4 && key.slice(0, 4).toLowerCase() === "data") {
    return dataDefault;
  }
}
function isRegexp(allowed) {
  return !!allowed && typeof allowed === "object" && "flags" in allowed;
}
class MdastToJsx {
  constructor({
    components = {}
  }) {
    __publicField(this, "mdast");
    __publicField(this, "str");
    __publicField(this, "c");
    __publicField(this, "errors", []);
    __publicField(this, "mdastHandlerList", []);
    __publicField(this, "sanitizeSchema");
    __publicField(this, "mapMdastChildren", (node) => {
      let children = node.children;
      if (children.length) {
        children = this.markChildren(this.flatChildren(children));
      }
      const res = children == null ? void 0 : children.flatMap(
        (child, index, arr) => {
          var _a;
          return this.mdastTransformer(
            child,
            index - 1 >= 0 ? (_a = arr[index - 1]) != null ? _a : null : null
          );
        }
      ).filter(Boolean);
      if (Array.isArray(res)) {
        if (!res.length) {
          return null;
        } else if (res.length === 1) {
          return res[0];
        } else {
          return res.map(
            (x, i) => React.isValidElement(x) ? cloneElement(x, { key: i }) : x
          );
        }
      }
      return res || null;
    });
    __publicField(this, "flatChildren", (childList) => {
      return childList.map((child) => {
        if ((child.type === "mdxJsxFlowElement" || child.type === "mdxJsxTextElement") && !child.name)
          return this.flatChildren(child.children);
        return child;
      }).flat(1);
    });
    __publicField(this, "markChildren", (childList) => {
      childList[0] = markFirstChild(childList[0]);
      childList[childList.length - 1] = markLastChild(
        childList[childList.length - 1]
      );
      return childList;
    });
    __publicField(this, "getJsxAttrs", (tagName2, node, onError = console.error) => {
      const attributes = this.sanitizeSchema.attributes;
      const specific = attributes && Object.prototype.hasOwnProperty.call(attributes, tagName2) ? attributes[tagName2] : void 0;
      const defaults = attributes && Object.prototype.hasOwnProperty.call(attributes, "*") ? attributes["*"] : void 0;
      const attrsList = node.attributes.map((attr) => {
        if (attr.type === "mdxJsxExpressionAttribute") {
          onError({
            message: `Expressions in jsx props are not supported (${attr.value.replace(
              /\n+/g,
              " "
            )})`
          });
          return;
        }
        if (attr.type !== "mdxJsxAttribute") {
          throw new Error(`non mdxJsxAttribute is not supported: ${attr}`);
        }
        const AttrName = transformHtmlAttr2JsxAttrName(attr.name);
        const definition = findDefinition(specific, AttrName) || findDefinition(defaults, AttrName);
        if (!definition)
          return;
        const AttrValue = attr.value;
        if (typeof AttrValue === "string") {
          return [AttrName, this.propertyValue(definition, AttrName, AttrValue)];
        }
        if (AttrValue === null) {
          return [AttrName, this.propertyValue(definition, AttrName, true)];
        }
        if ((AttrValue == null ? void 0 : AttrValue.type) === "mdxJsxAttributeValueExpression") {
          if (AttrValue.value === "true") {
            return [AttrName, this.propertyValue(definition, AttrName, true)];
          }
          if (AttrValue.value === "false") {
            return [AttrName, this.propertyValue(definition, AttrName, false)];
          }
          if (AttrValue.value === "null") {
            return [AttrName, this.propertyValue(definition, AttrName, null)];
          }
          if (AttrValue.value === "undefined") {
            return [
              AttrName,
              this.propertyValue(definition, AttrName, void 0)
            ];
          }
          if (AttrValue.value === "NaN") {
            return [AttrName, this.propertyValue(definition, AttrName, NaN)];
          }
          if (AttrValue.value === "Infinity") {
            return [
              AttrName,
              this.propertyValue(definition, AttrName, Infinity)
            ];
          }
          if (AttrValue.value === "-Infinity") {
            return [
              AttrName,
              this.propertyValue(definition, AttrName, -Infinity)
            ];
          }
          const quote = ['"', "'", "`"].find(
            (q) => AttrValue.value.startsWith(q) && AttrValue.value.endsWith(q)
          );
          if (quote) {
            let v = AttrValue.value;
            if (quote !== '"') {
              v = AttrValue.value.replace(new RegExp(quote, "g"), '"');
            }
            return [
              AttrName,
              this.propertyValue(definition, AttrName, safeJsonParse(v))
            ];
          }
          const number = Number(AttrValue.value);
          if (!isNaN(number)) {
            return [AttrName, this.propertyValue(definition, AttrName, number)];
          }
          const parsedJson = safeJsonParse(AttrValue.value);
          if (parsedJson) {
            return [
              AttrName,
              this.propertyValue(definition, AttrName, parsedJson)
            ];
          }
          onError({
            message: `Expressions in jsx props are not supported (${AttrName}={${AttrValue.value}})`
          });
        } else {
          console.log("unhandled attr", { attr }, attr.type);
        }
        return;
      }).filter(isTruthy);
      return attrsList;
    });
    __publicField(this, "jsxTransformer", (node, prev) => {
      if (!node) {
        return [];
      }
      switch (node.type) {
        case "mdxJsxTextElement":
        case "mdxJsxFlowElement": {
          const tagName2 = node.name;
          if (!tagName2 || typeof tagName2 !== "string") {
            return [];
          }
          const Component = this.c[tagName2];
          if (!Component) {
            this.errors.push({
              message: `Unsupported jsx component ${node.name}`
            });
            return null;
          }
          let safeElement = false;
          if (tagName2 && tagName2 !== "*" && (!this.sanitizeSchema.tagNames || this.sanitizeSchema.tagNames.includes(tagName2))) {
            safeElement = true;
          }
          const getChildren = () => this.mapJsxChildren(node);
          if (!safeElement) {
            return this.sanitizeSchema.strip && !this.sanitizeSchema.strip.includes(tagName2) ? /* @__PURE__ */ React.createElement(React.Fragment, null, getChildren()) : null;
          }
          const attrsList = this.getJsxAttrs(tagName2, node, (err) => {
            this.errors.push(err);
          });
          const attrs = Object.fromEntries(attrsList);
          if (isVoidElement(Component)) {
            return /* @__PURE__ */ React.createElement(
              Component,
              {
                ...attrs,
                ...calcRequireIndexProps({ source: node })
              }
            );
          }
          return /* @__PURE__ */ React.createElement(Component, { ...attrs, ...calcRequireIndexProps({ source: node }) }, getChildren());
        }
        default: {
          return this.mdastTransformer(node, prev);
        }
      }
    });
    __publicField(this, "mdastTransformer", (node, prev) => {
      if (!node) {
        return [];
      }
      const result = this.mdastHandlerList.reduce(
        (input, pluginItem, index) => {
          const re = pluginItem(input, prev);
          return re;
        },
        {
          source: node
        }
      );
      if ("res" in result)
        return result.res;
      switch (node.type) {
        case "mdxjsEsm": {
          return [];
        }
        case "mdxJsxTextElement":
        case "mdxJsxFlowElement": {
          try {
            const result2 = this.jsxTransformer(node, prev);
            if (Array.isArray(result2)) {
              console.log(`Unexpected array result`);
            } else if (result2) {
              return result2;
            }
          } finally {
          }
          return [];
        }
        case "mdxFlowExpression":
        case "mdxTextExpression": {
          if (!node.value) {
            return [];
          }
          return [];
        }
        default:
          mdastBfs(node, (node2) => {
            delete node2.position;
          });
          throw new Error(`cannot convert node` + JSON.stringify(node, null, 2));
      }
    });
    __publicField(this, "propertyValue", function(definition, key, raw) {
      let value = raw;
      if (definition === "href" || definition === "src") {
        value = String(raw);
      }
      if (key === "style") {
        try {
          return typeof value === "string" ? StyleToJS(value) : void 0;
        } catch (e) {
          return void 0;
        }
      }
      if (typeof value !== "string") {
        return value;
      }
      return definition ? this.propertyValuePrimitive(definition, key, value) : void 0;
    }.bind(this));
    __publicField(this, "safeProtocol", (key, value) => {
      const protocols = this.sanitizeSchema.protocols && Object.prototype.hasOwnProperty.call(this.sanitizeSchema.protocols, key) ? this.sanitizeSchema.protocols[key] : void 0;
      if (!protocols || protocols.length === 0) {
        return true;
      }
      const url = String(value);
      const colon = url.indexOf(":");
      const questionMark = url.indexOf("?");
      const numberSign = url.indexOf("#");
      const slash = url.indexOf("/");
      if (colon < 0 || slash > -1 && colon > slash || questionMark > -1 && colon > questionMark || numberSign > -1 && colon > numberSign) {
        return true;
      }
      let index = -1;
      while (++index < protocols.length) {
        const protocol = protocols[index];
        if (colon === protocol.length && url.slice(0, protocol.length) === protocol) {
          return true;
        }
      }
      return false;
    });
    __publicField(this, "propertyValuePrimitive", (definition, key, value) => {
      if (!this.safeProtocol(key, value)) {
        return;
      }
      if (typeof definition === "object" && definition.length > 1) {
        let ok = false;
        let index = 0;
        while (++index < definition.length) {
          const allowed = definition[index];
          if (isRegexp(allowed)) {
            if (allowed.test(String(value))) {
              ok = true;
              break;
            }
          } else if (allowed === value) {
            ok = true;
            break;
          }
        }
        if (!ok)
          return;
      }
      return this.sanitizeSchema.clobber && this.sanitizeSchema.clobberPrefix && this.sanitizeSchema.clobber.includes(key) ? this.sanitizeSchema.clobberPrefix + value : value;
    });
    this.c = {
      ...tagMap,
      ...components
    };
  }
  mapJsxChildren(node) {
    let children = node.children;
    if (node.type === "mdxJsxFlowElement" && children.length) {
      const [first, ...rest] = children;
      if (first.type === "paragraph") {
        children = [...first.children, ...rest];
      }
    }
    if (children.length) {
      children = this.markChildren(this.flatChildren(children));
    }
    const res = children == null ? void 0 : children.flatMap(
      (child, index, arr) => {
        var _a;
        return this.jsxTransformer(
          child,
          index - 1 >= 0 ? (_a = arr[index - 1]) != null ? _a : null : null
        );
      }
    ).filter(Boolean);
    if (Array.isArray(res)) {
      if (!res.length) {
        return null;
      } else if (res.length === 1) {
        return res[0];
      } else {
        return res.map(
          (x, i) => React.isValidElement(x) ? cloneElement(x, { key: i }) : x
        );
      }
    }
    return res || null;
  }
  run({
    code,
    mdast,
    components,
    sanitize,
    customHandler
  }) {
    var _a, _b, _c;
    this.errors = [];
    this.str = code;
    this.mdast = mdast;
    this.sanitizeSchema = (_a = sanitize == null ? void 0 : sanitize()) != null ? _a : defaultSchema;
    const sanitizeTagMap = (_c = (_b = this.sanitizeSchema.tagNames) == null ? void 0 : _b.reduce((res2, key) => {
      res2[key] = key;
      return res2;
    }, {})) != null ? _c : {};
    this.c = {
      ...sanitizeTagMap,
      ...tagMap,
      ...components
    };
    let identifierMap = void 0;
    const handlerCtx = {
      ctx: {
        calcRequireIndexProps,
        getIdentifierMap() {
          if (identifierMap)
            return identifierMap;
          const innerIdentifierMap = /* @__PURE__ */ new Map();
          mdastBfs(mdast, (child) => {
            if (child.type === "definition") {
              if (!innerIdentifierMap.has(child.identifier))
                innerIdentifierMap.set(child.identifier, child);
            }
          });
          return identifierMap = innerIdentifierMap;
        }
      },
      childrenFactory: this.mapMdastChildren,
      componentMap: this.c
    };
    this.mdastHandlerList = [
      ...customHandler != null ? customHandler : [],
      [
        thematicBreakHandler,
        headingHandler,
        codeHandler,
        htmlHandler,
        defHandler,
        linkRefHandler,
        paragraphHandler,
        tableHandler,
        tableRowHandler,
        tableCellHandler,
        tableHeadCellHandler
      ],
      [blockquoteHandler, listHandler, listItemHandler],
      [
        inlineCodeHandler,
        strongHandler,
        emphasisHandler,
        deleteHandler,
        linkHandler,
        imageHandler,
        imgRefHandler,
        breakHandler
      ],
      textHandler,
      rootHandler,
      [frontmatterHandler]
    ].flat(1).map((fn) => fn(handlerCtx));
    const res = this.mdastTransformer(this.mdast, null);
    if (Array.isArray(res) && res.length === 1) {
      return res[0];
    }
    return res;
  }
}
function isTruthy(val) {
  return Boolean(val);
}
function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch (err) {
    return null;
  }
}
const schemaStr = JSON.stringify(defaultSchema);
const SafeMdxViewer = ({
  sanitizeFn,
  plugins,
  value,
  customComponent,
  renderMdxError
}) => {
  var _a, _b, _c;
  const SanitizeSchema = useMemo(() => {
    var _a2;
    const nextSchema = JSON.parse(schemaStr);
    return (_a2 = sanitizeFn == null ? void 0 : sanitizeFn(nextSchema)) != null ? _a2 : nextSchema;
  }, [sanitizeFn]);
  const legacyParser = useMemo(
    () => getProcessor({
      plugins,
      sanitize: () => {
        return SanitizeSchema;
      }
    }),
    [SanitizeSchema, plugins]
  );
  const mdxParser = useMemo(() => getMdxProcessor({ plugins }), [plugins]);
  const safeMdxParser = useMemo(() => new MdastToJsx({}), []);
  const file = useMemo(() => {
    let mdxError = null;
    try {
      const preProcessValue = value.replaceAll("<br>", "<br/>").replaceAll("</br>", "").replaceAll("<hr>", "<hr/>").replaceAll("</hr>", "");
      const mdast = mdxParser.parse(preProcessValue);
      const mdxNode = safeMdxParser.run({
        code: value,
        mdast,
        sanitize: () => {
          return SanitizeSchema;
        },
        components: customComponent,
        customHandler: plugins == null ? void 0 : plugins.filter((item) => !!item.mdxHandlerList).map((item) => item.mdxHandlerList)
      });
      return {
        mdxNode,
        mdxError: null
      };
    } catch (err) {
      console.error(err);
      if (err instanceof Error) {
        mdxError = err.message;
      } else {
        mdxError = JSON.stringify(mdxError);
      }
    }
    try {
      const vfile = legacyParser.processSync(value);
      return {
        vfile,
        mdxError
      };
    } catch (err) {
      console.error("legacyParser", err);
      if (err instanceof Error) {
        mdxError = err.message;
      } else {
        mdxError = JSON.stringify(mdxError);
      }
    }
    try {
      const markedString = marked(value || "", {
        breaks: true,
        gfm: true,
        sanitize: false
      });
      return {
        markedString,
        mdxError
      };
    } catch (err) {
      console.error("marked", err);
      if (err instanceof Error) {
        mdxError = err.message;
      } else {
        mdxError = JSON.stringify(mdxError);
      }
    }
  }, [
    mdxParser,
    value,
    safeMdxParser,
    customComponent,
    plugins,
    SanitizeSchema,
    legacyParser
  ]);
  const [elRef, setElRef] = useState(null);
  useLayoutEffect(() => {
    const markdownBody = elRef;
    if (!markdownBody || !(file == null ? void 0 : file.vfile))
      return;
    const cbs = plugins == null ? void 0 : plugins.map(
      (options) => {
        var _a2;
        return (_a2 = options.viewerEffect) == null ? void 0 : _a2.call(options, { markdownBody, file: file.vfile });
      }
    );
    return () => {
      cbs == null ? void 0 : cbs.forEach((cb) => cb && cb());
    };
  }, [elRef, file, plugins]);
  if ((file == null ? void 0 : file.mdxError) === null) {
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        onClick: (e) => {
          var _a2, _b2;
          const $ = e.target;
          if ($.tagName !== "A")
            return;
          const href = $.getAttribute("href");
          if (!(href == null ? void 0 : href.startsWith("#")))
            return;
          (_b2 = (_a2 = e.currentTarget) == null ? void 0 : _a2.querySelector("#user-content-" + href.slice(1))) == null ? void 0 : _b2.scrollIntoView();
        },
        ref: setElRef,
        className: "markdown-body remark-mdx"
      },
      file.mdxNode
    );
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, renderMdxError && (file == null ? void 0 : file.mdxError) ? renderMdxError(file.mdxError) : null, /* @__PURE__ */ React.createElement(
    "div",
    {
      onClick: (e) => {
        var _a2, _b2;
        const $ = e.target;
        if ($.tagName !== "A")
          return;
        const href = $.getAttribute("href");
        if (!(href == null ? void 0 : href.startsWith("#")))
          return;
        (_b2 = (_a2 = e.currentTarget) == null ? void 0 : _a2.querySelector("#user-content-" + href.slice(1))) == null ? void 0 : _b2.scrollIntoView();
      },
      ref: setElRef,
      className: "markdown-body",
      dangerouslySetInnerHTML: {
        __html: (_c = (_b = (_a = file == null ? void 0 : file.vfile) == null ? void 0 : _a.toString()) != null ? _b : file == null ? void 0 : file.markedString) != null ? _c : ""
      }
    }
  ));
};
export {
  Editor,
  SafeMdxViewer
};
